import sortBy from "lodash/sortBy";
import startCase from "lodash/startCase";
import uniq from "lodash/uniq";
import { useTranslator } from "@eo-locale/react";

import { Box, Popover, Portal, Tooltip } from "@/storybook-components";
import { setCurrentPage, updateFinder } from "@/redux/features/projects/slice";
import Item from "@/theme/components/item";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import type { Project } from "@/types";

const ProjectTags = ({ project }: { project: Project }): JSX.Element => {
  const { queryFilter } = useAppSelector((state) => state.projectFinder);
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const { platformAnalyticsTagList } = project;
  const { filterByTag, showTagsFilter } = queryFilter;

  const handleClick = (value: string): void => {
    if (showTagsFilter) {
      dispatch(
        updateFinder({ platformAnalyticsTagList: value ? [value] : null })
      );
      dispatch(setCurrentPage(1));
    }
  };

  const renderProjectTag = (tags) => {
    tags = sortBy(tags);

    const initialTags = tags.slice(0, 3);
    const moreTags = tags.slice(3);

    const renderInitialTags = initialTags.map((tag) => (
      <Tooltip
        key={tag}
        hasArrow
        placement="top"
        borderRadius="md"
        fontFamily="inherit"
        aria-label={startCase(tag)}
        label={startCase(tag)}
        className="ehq-projectTagToolTip"
      >
        <Box
          px={2}
          py={1}
          mr="5px"
          tabIndex={0}
          bg="gray.100"
          fontSize="xs"
          minHeight={1}
          cursor="pointer"
          borderRadius="md"
          whiteSpace="nowrap"
          display="inline-block"
          overflow="hidden"
          className="ehq-projectTag"
          _focus={{ outline: "none", boxShadow: "outline" }}
          onClick={() => handleClick(tag)}
          data-tname={tag}
        >
          {startCase(tag)}
        </Box>
      </Tooltip>
    ));

    let renderMoreTags = null;
    if (moreTags.length > 0) {
      renderMoreTags = (
        <Popover.Root positioning={{ placement: "top-end" }}>
          <Popover.Trigger>
            <Box
              px={2}
              py={1}
              tabIndex={0}
              fontSize="xs"
              bg="gray.100"
              minHeight={1}
              cursor="pointer"
              borderRadius="md"
              display="inline-block"
              className="ehq-projectTag"
              _focus={{ outline: "none", boxShadow: "outline" }}
              aria-label={`${moreTags.length} ${translate(
                "projectFinder.tags.moreTags"
              )}`}
            >
              {`+${moreTags.length}`}
            </Box>
          </Popover.Trigger>
          <Portal>
            <Popover.Positioner>
              <Popover.Content
                zIndex={4}
                width="auto"
                className="ehq-projectTagPopover"
              >
                <Popover.Arrow />
                <Popover.Body pt={3}>
                  {moreTags.map((tag) => (
                    <Box
                      key={tag}
                      px={2}
                      py={1}
                      mr="5px"
                      tabIndex={0}
                      bg="gray.100"
                      fontSize="xs"
                      minHeight={1}
                      cursor="pointer"
                      borderRadius="md"
                      overflow="hidden"
                      whiteSpace="nowrap"
                      display="inline-block"
                      className="ehq-projectTag"
                      _focus={{ outline: "none", boxShadow: "outline" }}
                      onClick={() => handleClick(tag)}
                      data-tname={tag}
                    >
                      {startCase(tag)}
                    </Box>
                  ))}
                </Popover.Body>
              </Popover.Content>
            </Popover.Positioner>
          </Portal>
        </Popover.Root>
      );
    }
    return [renderInitialTags, renderMoreTags];
  };

  let tags = platformAnalyticsTagList;

  if (!tags || tags.length === 0) return null;

  tags = tags.map((tag) => tag.toLowerCase());

  // remove item from tags if pre-filter q.platformAnalyticsTagList is used
  if (filterByTag)
    tags = tags.filter((tag) => tag !== filterByTag.toLowerCase());

  return (
    <Item className="ehq-cardItem">
      <Box
        h="25px"
        display="flex"
        alignItems="center"
        className="ehq-projectTagsContainer"
      >
        {renderProjectTag(uniq(tags))}
      </Box>
      <style jsx global>{`
        .ehq-projectTag {
          text-overflow: ellipsis;
        }
      `}</style>
    </Item>
  );
};

export default ProjectTags;
